import api from "@/api";
import dayjs from "dayjs";

const initialEvent = {
  id: 0,
  title: "",
  location_id: null,
  start_date: new Date(),
  default_image_id: null,
  elements: [],
  duration: 0,
  interestgroup_ids: [],
  interestgroup: [],
  status: "pending",
  user_favourite: false,
  favourite_count: 0
};

export default {
  namespaced: true,

  state: {
    events: [],
    event: initialEvent,
    filters: {
      searchQuery: "",
      locations: [],
      size: null,
      published: false,
      tabFilter: ["approved"],
    },
    // tabFilter: '',
    sortBy: {
      field: "start_date",
      direction: "asc",
    },
    error: {},
    gridLayout: true,
    pagination: {
      total: 40,
      per_page: 40,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 40,
    },
  },
  mutations: {
    GET_EVENTS(state, payload) {
      state.events = payload;
    },
    GET_EVENT(state, payload) {
      state.event = payload;
    },
    SET_ERROR(state, payload) {
      state.error = payload;
    },
    SET_GRID_LAYOUT(state, layout) {
      state.gridLayout = layout;
    },
    SET_RESULT_COUNT(state, count) {
      state.filters.size = count;
    },
    RESET_EVENT(state) {
      state.event = { ...initialEvent };
    },
    SET_PUBLISHED_FILTER(state, enabled) {
      state.filters.published = enabled;
    },
    RESET_FILTERS(state) {
      state.filters = {
        searchQuery: "",
        categories: [],
        size: null,
        tags: [],
        isTrust: false,
        published: false,
        tabFilter: ["approved", "published"],
      };
    },
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total;
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page);
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page;
    },
    // not constant to match naming conventions used elsewhere
    setSearchQuery(state, payload) {
      state.filters.searchQuery = payload;
    },
    setTabFilter(state, tabFilter) {
      state.filters.tabFilter = tabFilter;
    },
    setFilterCategories(state, locations) {
      state.filters.locations = locations;
    },
  },
  actions: {
    async list({ commit, state }, options) {
      const localOptions = { ...options };
      const page = localOptions.page ? localOptions.page : 1;

      const size = state.pagination.per_page;
      let from = size * page - size;

      const today = dayjs().format("YYYY-MM-DD 00:00:00");
      const formatDate = dayjs(today).unix();

      if (!from) {
        from = 0;
      }
      const filter = {
        size,
        from,
        sort: [
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        query: {
          bool: {
            must: [
              {
                range: {
                  start_date: {
                    gte: formatDate,
                  },
                },
              },
            ],
          },
        },
      };

      if (state.filters.searchQuery) {
        filter.query.bool.must.push({
          bool: {
            should: [
              {
                match: {
                  title: {
                    query: state.filters.searchQuery,
                    operator: "and",
                    zero_terms_query: "all",
                    fuzziness: "AUTO",
                  },
                },
              },
              {
                match_phrase_prefix: {
                  title: {
                    query: state.filters.searchQuery,
                    slop: 50,
                  },
                },
              },
            ],
          },
        });
      }

      if (state.filters.size) {
        filter.size = state.filters.size;
      }

      if (state.filters.locations.length >= 1) {
        filter.query.bool.must.push({
          terms: {
            interestgroups: state.filters.locations,
          },
        });
      }

      if (state.filters.published && !state.filters.tabFilter) {
        filter.query.bool.must.push({
          term: {
            status: "approved",
          },
        });
      }

      if (state.filters.tabFilter) {
        filter.query.bool.must.push({
          terms: {
            status: state.filters.tabFilter,
          },
        });
      }

      let endpoint = "/schedule/events";

      if (options && options.isFlat) {
        endpoint = `${endpoint}/flat`;
      }

      let resp;
      try {
        resp = await api().put(endpoint, { filter });
        commit("GET_EVENTS", resp.data.data.event);
        commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
        commit("SET_CURRENT_PAGE", page);
        return Promise.resolve(resp.data.data.event);
      } catch (err) {
        if (
          err.response.status === 404 &&
          err.response.data.data.length === 0
        ) {
          commit("GET_EVENTS", []);
          return Promise.resolve([]);
        }
        commit("SET_ERROR", err.response.data.error);
        return Promise.reject(err.response.data.error);
      }
    },

    async query({ commit }, { query = {}, hidePast = false }) {
      let resp;

      if (hidePast) {
        query.query.bool.must.push({
          range: {
            start_date: {
              gt: dayjs().unix(),
            },
          },
        });
      }

      try {
        resp = await api().put("/schedule/events/flat", { filter: query });
        commit("GET_EVENTS", resp.data.data.event);
        commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
        commit("SET_CURRENT_PAGE", 1);
        return Promise.resolve(resp.data.data.event);
      } catch (error) {
        // If there are no articles it returns a 404, so set the response here
        if (error.response.data.data.length < 1) {
          commit("GET_EVENTS", []);
          return Promise.resolve([]);
        }
        commit(
          "SET_ERROR",
          error.status
            ? error.status
            : "Sorry, there was an error.  Please try again"
        );
        return Promise.reject(error);
      }
    },
    create({ dispatch, state, rootGetters }, pageData) {
      const newPageData = pageData || Object.assign({}, state.event);
      newPageData.elements = rootGetters["pageElements/forServer"] || null;
      return new Promise((resolve, reject) => {
        api()
          .post("/schedule/event", { event: newPageData })
          .then((page) => {
            dispatch(
              "notifications/create",
              {
                body: "Your event was created",
                type: "success",
              },
              { root: true }
            );
            resolve(page);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    /**
     * Approve an event
     */
    async approve({ dispatch }, id) {
      let response;

      try {
        response = await api().put(`/schedule/event/${id}/approve`);
        dispatch("read", id);
        return Promise.resolve(response.data.data.event);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    /**
     * Reject an event
     */
    async reject({ dispatch }, params) {
      let response;

      try {
        response = await api().put(`/schedule/event/${params.id}/reject`, {
          reject: { reason: params.reason },
        });
        dispatch("read", params.id);
        return Promise.resolve(response.data.data.event);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    async publish(context, id) {
      let response;

      try {
        response = await api().get(`/schedule/event/${id}/publish`);
        return Promise.resolve(response);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    /**
     * Update an event
     */
    update({ commit, dispatch, rootGetters, state }, { pageId, pageData }) {
      const newPageData = pageData || Object.assign({}, state.event);
      newPageData.elements = rootGetters["pageElements/forServer"] || null;

      return new Promise((resolve, reject) => {
        api()
          .put(`/schedule/event/${pageId}`, { event: newPageData })
          .then((page) => {
            commit("GET_EVENT", page.data.data.event[0]);
            dispatch(
              "notifications/create",
              {
                body: "The event was updated",
                type: "success",
              },
              { root: true }
            );
            resolve(page);
          })
          .catch((error) => {
            if (error.error.status_code === 400) {
              dispatch(
                "notifications/create",
                {
                  title: "Error!",
                  body: error.error.message,
                  type: "danger",
                },
                { root: true }
              );
            }
            reject(error);
          });
      });
    },

    // resubmit an event
    resubmit({ dispatch }, { pageId, pageData }) {
      dispatch("update", {
        pageId,
        pageData,
      }).then(() => {
        api()
          .put(`/schedule/event/${pageId}/resubmit`)
          .then(() => {
            dispatch("read", pageId);
          });
      });
    },

    /**
     * Get a specific event
     */
    read({ commit }, id) {
      return new Promise((resolve, reject) => {
        api()
          .get(`/schedule/event/${id}`)
          .then((resp) => {
            commit("GET_EVENT", resp.data.data.event[0]);
            resolve(resp.data.data.event[0]);
          })
          .catch((error) => {
            commit(
              "SET_ERROR",
              error.status
                ? error.status
                : "Sorry, there was an error.  Please try again"
            );
            reject(error.response.data);
          });
      });
    },

    /**
     * Delete an event
     */
    delete({ commit, dispatch }, id) {
      api()
        .delete(`/schedule/event/${id}`)
        .then(() => {
          dispatch("list");
          dispatch(
            "notifications/create",
            {
              body: "The event was deleted",
              type: "success",
            },
            { root: true }
          );
        })
        .catch((err) => {
          /**
           * In the case we have deleted the last item in the list
           */
          dispatch("list");
          commit("SET_ERROR", err);
        });
    },

    /**
     * determine if the news index page displays in a grid or a list
     *
     */
    toggleGrid({ commit }, value) {
      commit("SET_GRID_LAYOUT", value);
    },

    async favourite({ state }, eventId) {
      let resp;

      try {
        resp = await api().put(`/schedule/event/${eventId}/favourite`);
        state.event.user_favourite = true;
        state.event.favourite_count++;
        return Promise.resolve();
      } catch (err) {
        console.log("🚀 ~ file: events.js ~ line 434 ~ favourite ~ err", err);
        return Promise.reject();
      }
    },

    async unfavourite({ state }, eventId) {
      let resp;

      try {
        resp = await api().put(`/schedule/event/${eventId}/unfavourite`);
        state.event.user_favourite = false;
        state.event.favourite_count--;
        return Promise.resolve();
      } catch (err) {
        console.log("🚀 ~ file: events.js ~ line 434 ~ favourite ~ err", err);
        return Promise.reject();
      }
    },
  },
  getters: {},
};
